import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DomainRoundedIcon from '@mui/icons-material/DomainRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import { Alert, Box, Button, IconButton, Stack, Switch, Typography } from '@mui/material';

import { useTenantSettings, useUpdateTenantSettings } from '../../hooks';
import {
  emptyContactInformation,
  toSettingsUpdate,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
} from '../../lib';
import type {
  BankAccount,
  ContactInformation,
  LegalEntityInformation,
} from '../../model/settingsTypes';
import { CURRENCY_OPTIONS } from '../options';
import {
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  TextInputField,
} from '../components';
import { FeedbackSnackbar } from '../components/FeedbackSnackbar';

function emptyLegalEntity(): LegalEntityInformation {
  return {
    legalForm: '',
    legalName: '',
    inn: '',
    kpp: '',
    ogrn: '',
    okved: '',
    taxSystem: '',
    legalAddress: '',
    actualAddress: '',
    directorName: '',
    chiefAccountant: '',
    authorizedSignerName: '',
    vatNote: '',
  };
}

function ContactInformationSection({
  value,
  onChange,
  disabled,
}: {
  value: ContactInformation;
  onChange: (value: ContactInformation) => void;
  disabled: boolean;
}) {
  return (
    <SettingsCard
      title="Contacts"
      description="Public company contacts shown to customers and used for storefront resolution."
      icon={<ContactsRoundedIcon />}
    >
      <TextInputField
        label="Phone"
        value={value.phone ?? ''}
        onChange={(next) => onChange({ ...value, phone: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Email"
        value={value.email ?? ''}
        onChange={(next) => onChange({ ...value, email: next })}
        disabled={disabled}
        type="email"
      />
    </SettingsCard>
  );
}

function LegalEntitySection({
  value,
  onChange,
  disabled,
}: {
  value: LegalEntityInformation;
  onChange: (value: LegalEntityInformation) => void;
  disabled: boolean;
}) {
  return (
    <SettingsCard
      title="Legal entity"
      description="The merchant's public legal profile shown on the storefront and legal pages."
      icon={<DomainRoundedIcon />}
    >
      <TextInputField
        label="Legal form"
        value={value.legalForm ?? ''}
        onChange={(next) => onChange({ ...value, legalForm: next })}
        disabled={disabled}
        placeholder="e.g. LLC / OOO"
      />
      <TextInputField
        label="Legal name"
        value={value.legalName ?? ''}
        onChange={(next) => onChange({ ...value, legalName: next })}
        disabled={disabled}
      />
      <TextInputField
        label="INN"
        value={value.inn ?? ''}
        onChange={(next) => onChange({ ...value, inn: next })}
        disabled={disabled}
      />
      <TextInputField
        label="KPP"
        value={value.kpp ?? ''}
        onChange={(next) => onChange({ ...value, kpp: next })}
        disabled={disabled}
      />
      <TextInputField
        label="OGRN"
        value={value.ogrn ?? ''}
        onChange={(next) => onChange({ ...value, ogrn: next })}
        disabled={disabled}
      />
      <TextInputField
        label="OKVED"
        description="ОКВЭД"
        value={value.okved ?? ''}
        onChange={(next) => onChange({ ...value, okved: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Tax system"
        description="Система налогообложения"
        value={value.taxSystem ?? ''}
        onChange={(next) => onChange({ ...value, taxSystem: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Legal address"
        value={value.legalAddress ?? ''}
        onChange={(next) => onChange({ ...value, legalAddress: next })}
        disabled={disabled}
        multiline
        rows={2}
      />
      <TextInputField
        label="Actual address"
        value={value.actualAddress ?? ''}
        onChange={(next) => onChange({ ...value, actualAddress: next })}
        disabled={disabled}
        multiline
        rows={2}
      />
      <TextInputField
        label="Director name"
        value={value.directorName ?? ''}
        onChange={(next) => onChange({ ...value, directorName: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Chief accountant"
        description="Главный бухгалтер"
        value={value.chiefAccountant ?? ''}
        onChange={(next) => onChange({ ...value, chiefAccountant: next })}
        disabled={disabled}
      />
      <TextInputField
        label="Authorized signer"
        description="Лицо, уполномоченное подписывать договоры по доверенности"
        value={value.authorizedSignerName ?? ''}
        onChange={(next) => onChange({ ...value, authorizedSignerName: next })}
        disabled={disabled}
      />
      <TextInputField
        label="VAT note"
        description="Short tax note shown to customers, e.g. 'VAT not applicable'."
        value={value.vatNote ?? ''}
        onChange={(next) => onChange({ ...value, vatNote: next })}
        disabled={disabled}
      />
    </SettingsCard>
  );
}

function BankAccountsSection({
  value,
  onChange,
  disabled,
}: {
  value: BankAccount[];
  onChange: (value: BankAccount[]) => void;
  disabled: boolean;
}) {
  const updateAccount = (index: number, patch: Partial<BankAccount>) => {
    onChange(
      value.map((account, currentIndex) =>
        currentIndex === index ? { ...account, ...patch } : account,
      ),
    );
  };

  const removeAccount = (index: number) => {
    const next = value.filter((_, currentIndex) => currentIndex !== index);
    if (next.length > 0 && !next.some((account) => account.defaultAccount)) {
      next[0] = { ...next[0], defaultAccount: true };
    }
    onChange(next);
  };

  const addAccount = () => {
    onChange([
      ...value,
      {
        id: null,
        label: '',
        bankName: '',
        bik: '',
        correspondentAccount: '',
        accountNumber: '',
        currency: '',
        beneficiaryName: '',
        defaultAccount: value.length === 0,
        position: value.length,
      },
    ]);
  };

  const setDefaultAccount = (index: number, checked: boolean) => {
    const next = value.map((account, currentIndex) =>
      checked
        ? { ...account, defaultAccount: currentIndex === index }
        : currentIndex === index
          ? { ...account, defaultAccount: false }
          : account,
    );
    // If unchecking left no default, auto-promote the first account so the UI
    // always reflects what the server will do on save (fallback to index 0).
    if (!checked && !next.some((a) => a.defaultAccount) && next.length > 0) {
      next[0] = { ...next[0], defaultAccount: true };
    }
    onChange(next);
  };

  return (
    <SettingsCard
      title="Bank accounts"
      description="Settlement accounts merchants can show on the site. Keep one account marked as default."
      icon={<AccountBalanceRoundedIcon />}
    >
      <Alert severity="info" variant="outlined">
        The list is saved as-is. If no account is marked default, the first account is treated as
        fallback.
      </Alert>
      {value.length === 0 ? (
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No bank accounts yet. Add the first settlement account to start publishing company
            details.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          {value.map((account, index) => (
            <SettingsCard
              key={account.id ?? `${account.accountNumber ?? 'new'}-${index}`}
              title={account.label?.trim() || `Account ${index + 1}`}
              description={
                account.accountNumber?.trim()
                  ? `Account number ${account.accountNumber.trim()}`
                  : 'Fill the settlement details below.'
              }
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Default
                  </Typography>
                  <Switch
                    checked={account.defaultAccount}
                    disabled={disabled}
                    onChange={(event) => setDefaultAccount(index, event.target.checked)}
                    slotProps={{ input: { 'aria-label': `Default account ${index + 1}` } }}
                  />
                  <IconButton
                    aria-label={`Remove bank account ${index + 1}`}
                    onClick={() => removeAccount(index)}
                    disabled={disabled}
                    color="error"
                    size="small"
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              }
            >
              <TextInputField
                label="Label"
                description="A friendly name for merchants, e.g. Main operating account."
                value={account.label ?? ''}
                onChange={(next) => updateAccount(index, { label: next })}
                disabled={disabled}
              />
              <TextInputField
                label="Bank name"
                value={account.bankName ?? ''}
                onChange={(next) => updateAccount(index, { bankName: next })}
                disabled={disabled}
              />
              <TextInputField
                label="BIK"
                value={account.bik ?? ''}
                onChange={(next) => updateAccount(index, { bik: next })}
                disabled={disabled}
              />
              <TextInputField
                label="Correspondent account"
                value={account.correspondentAccount ?? ''}
                onChange={(next) => updateAccount(index, { correspondentAccount: next })}
                disabled={disabled}
              />
              <TextInputField
                label="Account number"
                value={account.accountNumber ?? ''}
                onChange={(next) => updateAccount(index, { accountNumber: next })}
                disabled={disabled}
              />
              <SelectField
                label="Currency"
                value={account.currency ?? ''}
                options={CURRENCY_OPTIONS}
                onChange={(next) => updateAccount(index, { currency: next })}
                disabled={disabled}
                allowEmpty
              />
              <TextInputField
                label="Beneficiary name"
                value={account.beneficiaryName ?? ''}
                onChange={(next) => updateAccount(index, { beneficiaryName: next })}
                disabled={disabled}
              />
              <NumberField
                label="Position"
                description="Optional manual order; blank values fall back to the list order."
                value={account.position}
                onChange={(next) => updateAccount(index, { position: next })}
                disabled={disabled}
                min={0}
              />
            </SettingsCard>
          ))}
        </Stack>
      )}
      <Box sx={{ pt: 1 }}>
        <Button
          startIcon={<AddRoundedIcon />}
          onClick={addAccount}
          disabled={disabled}
          size="small"
        >
          Add bank account
        </Button>
      </Box>
    </SettingsCard>
  );
}

/** Company profile settings: contacts, legal entity and bank accounts. */
export function TenantSettingsPage() {
  const { canView, canManage } = useDomainPermissions('tenant');
  const query = useTenantSettings();
  const update = useUpdateTenantSettings();
  const form = useSettingsForm(query.data);
  const { feedback, propagation, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => propagation('Company settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Company settings"
      subtitle="Contacts, legal entity and bank accounts."
      icon={<DomainRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        const contactInformation = draft.contactInformation ?? emptyContactInformation();
        const legalEntity = draft.legalEntityInformation ?? emptyLegalEntity();
        const bankAccounts = draft.bankAccounts ?? [];

        return (
          <Stack spacing={2.5}>
            <ContactInformationSection
              value={contactInformation}
              onChange={(value) => form.setField('contactInformation', value)}
              disabled={disabled}
            />

            <LegalEntitySection
              value={legalEntity}
              onChange={(value) => form.setField('legalEntityInformation', value)}
              disabled={disabled}
            />

            <BankAccountsSection
              value={bankAccounts}
              onChange={(value) => form.setField('bankAccounts', value)}
              disabled={disabled}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
